/**
 * Recorte da fila de curadoria para o Consolidador.
 *
 * O `GET /suggestions` devolve a fila inteira do escopo, com tarefa, oportunidade e objetivo
 * vindos de transcrição, porque ainda ignora o filtro por entidade. Sem este recorte a carteira
 * mostraria sugestão de CRM na linha do ativo. Quando o backend filtrar, estas funções ficam
 * redundantes — nunca erradas — e saem.
 *
 * Contagem devolve `null` quando a fila não foi apurada (erro, backend fora do ar) e número
 * quando foi: o badge precisa distinguir "não sei" de "zero", e afirmar zero sem ter apurado é o
 * defeito que os testes deste arquivo travam. `pendentesPorAtivo` existe para a tabela não pagar
 * uma varredura por linha. Ela é chamada pelo COMPONENTE, não pela classe de API: a de movimentação
 * precisa do de-para movimentação→ativo, que só a tela tem. A API devolvia essa contagem pronta e
 * isso virou armadilha — o campo continuava certo na aparência e passou a subcontar quando a
 * movimentação entrou na fila, então saiu de lá.
 *
 * Status fora dos três que o painel renderiza é descartado, em vez de virar uma quarta chave que
 * sumiria da tela sem aviso.
 */
import { TABELAS_DO_CONSOLIDADOR } from '@modules/consolidator/constants/suggestions'
import { categoriaAncoraNoAtivo, ehDeMovimentacao } from '@modules/consolidator/utils/suggestions-categoria/suggestions-categoria'

export const STATUS_AGUARDANDO = 'aguardando'
export const STATUS_ACEITA = 'aceita'
export const STATUS_DECLINADA = 'declinada'

const porStatusVazio = () => ({
  [STATUS_AGUARDANDO]: [],
  [STATUS_ACEITA]: [],
  [STATUS_DECLINADA]: [],
})

export const apenasDoConsolidador = (suggestions) => {
  const lista = Array.isArray(suggestions) ? suggestions : []
  return lista.filter((sugestao) =>
    TABELAS_DO_CONSOLIDADOR.includes(sugestao && sugestao.tabela_referencia)
  )
}

export const agruparPorStatus = (suggestions) => {
  const grupos = porStatusVazio()
  const lista = Array.isArray(suggestions) ? suggestions : []
  lista.forEach((sugestao) => {
    const status = sugestao && sugestao.status
    if (grupos[status]) grupos[status].push(sugestao)
  })
  return grupos
}

/** Conjunto de ids da carteira, para o resolvedor recusar alvo que nao esta nela. */
export const idsDosAtivos = (ativos) =>
  new Set((ativos || []).filter(Boolean).map((a) => String(a.id)))

/**
 * ★★ FONTE ÚNICA de "de qual ativo é esta sugestão". A contagem do selo e o filtro do painel
 * derivam DAQUI, porque tê-los como duas implementações foi o que produziu o pior defeito da
 * feature: o selo somava as de movimentação e o painel, que filtrava por `id_entidade_alvo`,
 * abria vazio — "2 pendentes" e nenhuma sugestão na tela.
 *
 * ★ A sugestão de MOVIMENTAÇÃO também é sobre um ativo — ela só não diz qual. O `id_entidade_alvo`
 * dela é a PK da movimentação, então sem o de-para ela não aparecia na linha do ativo e o assessor
 * só descobria o problema entrando na aba. Passando `movimentacoes` (índice
 * `{ <id_aplicacao>: { movimentacao, ativo } }`), ela conta na linha do ativo dono. Sem o índice o
 * comportamento é o de antes: só as ancoradas contam, nunca uma linha errada.
 */
export const idDoAtivoDaSugestao = (
  suggestion,
  { movimentacoes = null, ativos = null } = {}
) => {
  const id = idBrutoDoAtivo(suggestion, movimentacoes)
  if (id === null) return null
  // Conjunto ausente ou VAZIO = carteira ainda nao apurada. Nao filtra: filtrar aqui zeraria a
  // contagem antes de a carteira responder, que e exatamente o "afirmar numero nao apurado" que o
  // resto do fluxo evita.
  if (!ativos || !ativos.size) return id
  // A sugestao pode apontar um ativo que saiu da carteira (removido, ou fora do periodo). Contar
  // esse alvo fazia o alerta dizer "N ativos desta carteira" incluindo um que o painel nao sabe
  // nomear nem editar — o clique em "Editar e aceitar" morria em silencio.
  return ativos.has(id) ? id : null
}

const idBrutoDoAtivo = (suggestion, movimentacoes) => {
  const alvo = suggestion && suggestion.id_entidade_alvo
  if (alvo === null || alvo === undefined || alvo === '') return null
  if (categoriaAncoraNoAtivo(suggestion)) return String(alvo)
  if (!movimentacoes || !ehDeMovimentacao(suggestion)) return null
  const entrada = movimentacoes[String(alvo)]
  const mov = entrada && entrada.movimentacao
  return mov && mov.id_ativo_cliente ? String(mov.id_ativo_cliente) : null
}

/**
 * Contagem por MOVIMENTAÇÃO, para o Extrato marcar a linha exata (`Resgate de 12/12`) em vez de
 * só o ativo. Aqui a chave é o próprio `id_entidade_alvo`, que já É a PK da movimentação — não
 * precisa de de-para nenhum.
 */
export const pendentesPorMovimentacao = (suggestions) => {
  if (!Array.isArray(suggestions)) return null
  const contagem = {}
  apenasDoConsolidador(suggestions).forEach((sugestao) => {
    if (sugestao.status !== STATUS_AGUARDANDO) return
    if (!ehDeMovimentacao(sugestao)) return
    const alvo = sugestao.id_entidade_alvo
    if (alvo === null || alvo === undefined || alvo === '') return
    contagem[String(alvo)] = (contagem[String(alvo)] || 0) + 1
  })
  return contagem
}

export const pendentesPorAtivo = (
  suggestions,
  { movimentacoes = null, ativos = null } = {}
) => {
  if (!Array.isArray(suggestions)) return null
  const contagem = {}
  apenasDoConsolidador(suggestions).forEach((sugestao) => {
    if (sugestao.status !== STATUS_AGUARDANDO) return
    const idAtivo = idDoAtivoDaSugestao(sugestao, { movimentacoes, ativos })
    if (!idAtivo) return
    contagem[idAtivo] = (contagem[idAtivo] || 0) + 1
  })
  return contagem
}
